import type { ErrorCopy } from '@shared/errors/catalog';

/**
 * The `error_code` values the three subscription surfaces meet, kept in one
 * place because all three talk to the same service and two of them raise the
 * same codes. Codes more than one **feature** meets live in
 * `@shared/errors/catalog.ts`; these are this feature's.
 *
 * **Never inline a backend code in JSX.** The code is a log key; what reaches
 * the operator is the sentence, and the `X-Request-ID` is what they quote.
 *
 * Tone is load-bearing. An upstream failure nobody in this console caused is
 * **neutral** — a danger-styled banner on a peer outage teaches an operator that
 * they broke something. `warning` marks a refusal the operator can act on;
 * `danger` is reserved for a destructive action that actually failed.
 */

export const financeErrors: Readonly<Record<string, ErrorCopy>> = {
  FINANCE_FORBIDDEN: {
    message: 'هذا السطح يحتاج دوراً أوسع في الاشتراكات، ولا يحمله حسابك.',
    tone: 'warning',
  },
  FINANCE_NOT_FOUND: {
    message: 'لم نعثر على هذا السجلّ. قد يكون حُذف بينما كانت الصفحة مفتوحة.',
    tone: 'warning',
  },
  FINANCE_BAD_INPUT: {
    message: 'راجع الحقول المعلَّمة ثمّ أعد الإرسال.',
    tone: 'warning',
  },
  FINANCE_CONFLICT: {
    message: 'تغيّرت حالة السجلّ بينما كانت الصفحة مفتوحة. أعد التحميل ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  /**
   * The activation refusal. It is not «configure the gateway» in the abstract —
   * the credential row is what is unset, and rotation is the control that sets
   * it, so the copy names that control by the words on it.
   */
  FINANCE_GATEWAY_NOT_CONFIGURED: {
    message: 'لا مفتاح محفوظاً لهذه البوّابة. استخدم «دوّر بيانات الاعتماد» أولاً ثمّ فعّلها.',
    tone: 'warning',
  },
  /**
   * Deleting the bound provider would leave the checkout hand-off with no
   * target. The console already disables the entry on that row; this is the
   * copy for the case where the row moved between load and click.
   */
  FINANCE_GATEWAY_ACTIVE_CANNOT_DELETE: {
    message: 'هذه هي البوّابة الفاعلة. فعّل بوّابة أخرى ثمّ احذف هذه.',
    tone: 'warning',
  },
  /**
   * The ledger's ending, refused because the row is already terminal.
   *
   * It names the state rather than the rule: an operator reading «هذا السجلّ
   * منتهٍ» knows to reload, while «انتقال غير صالح» is the service's vocabulary
   * for a machine the console does not draw.
   */
  FINANCE_SUBSCRIPTION_INVALID_TRANSITION: {
    message: 'هذا الاشتراك انتهى أو أُلغي بالفعل، فلا شيء يُنهى. أعد تحميل الصفحة لترى حالته الحالية.',
    tone: 'warning',
  },
  /**
   * A grant lift aimed at an entitlement a **subscription** produced. The copy
   * is the way out, not the rule: the operator wanted to end the access and the
   * sentence names the control that does it.
   */
  FINANCE_ENTITLEMENT_NOT_REVOCABLE: {
    message: 'هذا الاستحقاق يتبع اشتراكاً مبيعاً، فلا يُرفع وحده. ألغِ الاشتراك نفسه من هذا الجدول.',
    tone: 'warning',
  },
  FINANCE_NO_ACTIVE_GATEWAY: {
    message: 'لا بوّابة فاعلة الآن، فلا اشتراك جديد يُشترى. فعّل واحدة من هذا الجدول.',
    tone: 'warning',
  },
  FINANCE_RATE_LIMITED: {
    message: 'محاولات كثيرة في وقت قصير. انتظر قليلاً ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  FINANCE_PSP_UNAVAILABLE: {
    message: 'لم يجب المزوّد. أعد المحاولة بعد قليل.',
    tone: 'neutral',
  },
  /**
   * A timeout on a money-moving call is an **unknown**, not a failure, and the
   * copy says so: it directs to a reload rather than offering a retry the
   * operator would press twice.
   */
  FINANCE_PSP_TIMEOUT: {
    message: 'تأخّر المزوّد عن الردّ ولا نعرف نتيجة العملية. أعد تحميل الصفحة للاطّلاع عليها.',
    tone: 'neutral',
  },
  FINANCE_UPSTREAM_FAILED: {
    message: 'الخدمة غير متاحة الآن. أعد المحاولة بعد قليل.',
    tone: 'neutral',
  },
  FINANCE_UPSTREAM_TIMEOUT: {
    message: 'تأخّرت الخدمة عن الردّ. أعد تحميل الصفحة للاطّلاع على النتيجة.',
    tone: 'neutral',
  },
};

/**
 * The analytics codes the reporting surface meets on its aggregate band.
 *
 * `ANALYTICS_REVENUE_UNAVAILABLE` is the sharpest of them: the service refuses
 * to substitute a zero for a money figure it could not obtain, and the copy has
 * to keep that promise on the screen. An operator reading `٠` where the number
 * is unknown is worse than a stated failure.
 */
export const analyticsErrors: Readonly<Record<string, ErrorCopy>> = {
  ANALYTICS_FORBIDDEN: {
    message: 'قراءة هذه الأرقام تحتاج دوراً أوسع. راجع مسؤولاً.',
    tone: 'warning',
  },
  ANALYTICS_REVENUE_UNAVAILABLE: {
    message: 'أرقام الإيراد غير متاحة الآن، ولا تُعرض أرقام تقريبية بدلاً منها. أعد المحاولة بعد قليل.',
    tone: 'neutral',
  },
  ANALYTICS_UPSTREAM_FAILED: {
    message: 'إحدى الخدمات لم تجب. أعد المحاولة بعد قليل.',
    tone: 'neutral',
  },
  ANALYTICS_UPSTREAM_TIMEOUT: {
    message: 'تأخّرت إحدى الخدمات عن الردّ. أعد المحاولة بعد قليل.',
    tone: 'neutral',
  },
};
